/**
 * SOAR Cart Main — full /cart page interactions
 * - +/- buttons sync the qty input + AJAX update via /cart/change.js
 * - Direct input edits debounce-update
 * - Remove button sets quantity to 0
 * - Dispatches cart:updated CustomEvent so the header badge stays in sync
 * - Falls back to full form submit if AJAX fails
 */
(function () {
  const moneyFormat = window.SOAR_MONEY_FORMAT || '${{amount}}';

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return moneyFormat.replace('{{amount}}', dollars).replace('{{ amount }}', dollars);
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class SoarCartMain extends HTMLElement {
    connectedCallback() {
      this.subtotalEl = this.querySelector('[data-soar-cart-subtotal]');
      this.itemsContainer = this.querySelector('[data-soar-cart-items]');
      this.bindControls();
    }

    bindControls() {
      // +/- buttons
      this.querySelectorAll('[data-soar-cart-qty]').forEach((btn) => {
        btn.addEventListener('click', (e) => this.onQtyButton(e));
      });

      // direct input changes (debounced)
      const debouncedUpdate = debounce((key, qty) => this.changeLine(key, qty), 400);
      this.querySelectorAll('[data-soar-cart-qty-input]').forEach((input) => {
        input.addEventListener('change', (e) => {
          const itemEl = e.target.closest('[data-line-key]');
          if (!itemEl) return;
          const qty = Math.max(0, parseInt(e.target.value, 10) || 0);
          debouncedUpdate(itemEl.dataset.lineKey, qty);
        });
      });

      // remove buttons
      this.querySelectorAll('[data-soar-cart-remove]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          const itemEl = e.target.closest('[data-line-key]');
          if (!itemEl) return;
          this.changeLine(itemEl.dataset.lineKey, 0, itemEl);
        });
      });
    }

    onQtyButton(e) {
      const btn = e.currentTarget;
      const itemEl = btn.closest('[data-line-key]');
      if (!itemEl) return;
      const input = itemEl.querySelector('[data-soar-cart-qty-input]');
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const delta = btn.dataset.soarCartQty === 'increase' ? 1 : -1;
      const next = Math.max(0, current + delta);
      input.value = next;
      this.changeLine(itemEl.dataset.lineKey, next, itemEl);
    }

    async changeLine(id, quantity, itemEl) {
      this.dataset.loading = 'true';
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id, quantity: Math.max(0, quantity) })
        });
        if (!res.ok) throw new Error('cart change failed');
        const cart = await res.json();
        this.afterUpdate(cart, id, quantity, itemEl);
      } catch (err) {
        console.error('SOAR cart-main change failed', err);
        // fallback: reload to re-render server-side
        window.location.reload();
      } finally {
        this.dataset.loading = 'false';
      }
    }

    afterUpdate(cart, changedKey, quantity, itemEl) {
      // Update subtotal
      if (this.subtotalEl) {
        this.subtotalEl.textContent = formatMoney(cart.total_price);
      }

      // If qty 0, remove the row
      if (quantity === 0 && itemEl) {
        itemEl.remove();
      } else if (changedKey) {
        // Update the line price for the changed item
        const updated = cart.items.find((i) => i.key === changedKey);
        const row = this.querySelector(`[data-line-key="${CSS.escape(changedKey)}"]`);
        if (row && updated) {
          const priceEl = row.querySelector('.soar-cart-main__item-price');
          if (priceEl) {
            priceEl.firstChild && (priceEl.firstChild.textContent = formatMoney(updated.final_line_price));
            // Re-render compare price if needed
            const compareEl = priceEl.querySelector('.soar-cart-main__item-price-compare');
            if (compareEl) {
              if (updated.original_line_price > updated.final_line_price) {
                compareEl.textContent = formatMoney(updated.original_line_price);
              } else {
                compareEl.remove();
              }
            } else if (updated.original_line_price > updated.final_line_price) {
              const span = document.createElement('span');
              span.className = 'soar-cart-main__item-price-compare';
              span.textContent = formatMoney(updated.original_line_price);
              priceEl.appendChild(span);
            }
            // Set plain text price (rebuild)
            priceEl.childNodes.forEach((n) => {
              if (n.nodeType === Node.TEXT_NODE) n.textContent = formatMoney(updated.final_line_price);
            });
          }
          const mobilePriceEl = row.querySelector('.soar-cart-main__item-price-mobile');
          if (mobilePriceEl) {
            mobilePriceEl.textContent = formatMoney(updated.final_line_price);
          }
        }
      }

      // If cart now empty, reload to show empty state
      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      // Notify header + drawer
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { itemCount: cart.item_count, totalPrice: cart.total_price }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  if (!customElements.get('soar-cart-main')) {
    customElements.define('soar-cart-main', SoarCartMain);
  }
})();
